import { v4 as uuidv4 } from 'uuid';
import { getGridStringOperators, GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import { ActionMenu } from '../ActionMenu/ActionMenu';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';
import Button from '@mui/material/Button';
import { getScreenData, Store, ACTIVE_SCREEN_KEY } from 'cw-ui-core';
import { toArray } from 'src/util/Util';
import { GridRowModes } from '@mui/x-data-grid-pro';
import { Tooltip } from '@cw-ui/data-display-components';

const Columns = (rowModesModel, setRowModesModel) => {
    const equalsOperator = getGridStringOperators().filter(operator => operator.value === 'equals');
    const loginProviders = toArray(getScreenData(Store().getState().screen, ['loginProviders'], ACTIVE_SCREEN_KEY));

    const handleEditClick = id => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
    };

    const handleCancelClick = id => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        });
    };

    const handleSaveClick = id => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const loginProviderColumn = row => {
        const loginProvider = loginProviders.find(lp => lp.scheme === row.loginScheme);
        if (loginProvider?.clientName) {
            return loginProvider.clientName;
        } else {
            if (row.loginScheme) {
                return 'Invalid';
            } else if (row.loginScheme === '') {
                return 'Local login';
            } else if (row.loginScheme === null) {
                if (!(row.errorMessage && row?.errorMessage.includes('LoginProvider'))) {
                    return 'Local login';
                }
                return 'Invalid';
            }
            return 'Invalid';
        }
    };

    const mfaTypeColumn = row => {
        const loginProviderValue = loginProviderColumn(row);
        if (loginProviderValue !== 'Local login') {
            return 'N/A';
        } else {
            if (row.mfaType.toLowerCase() === 'auth' || row.mfaType.toLowerCase() === 'email') {
                return row.mfaType;
            }
            return 'Invalid';
        }
    };

    return [
        {
            ...GRID_CHECKBOX_SELECTION_COL_DEF,
            hideable: false, // Prevents the column from being hidden via the UI
            headerName: 'Checkbox Selection', // Optional: customize the header name
        },
        {
            field: 'firstName',
            headerName: 'First Name',
            id: uuidv4(),
            type: 'string',
            width: 110,
            editable: true,
            filterable: true,
            filterOperators: equalsOperator,
        },
        {
            field: 'middleName',
            headerName: 'Middle Name',
            id: uuidv4(),
            type: 'string',
            editable: true,
            width: 130,
            filterable: false,
        },
        {
            field: 'lastName',
            headerName: 'Last Name',
            id: uuidv4(),
            type: 'string',
            editable: true,
            width: 120,
            filterable: true,
            filterOperators: equalsOperator,
        },
        {
            field: 'status',
            headerName: 'Status',
            id: uuidv4(),
            type: 'string',
            width: 100,
            filterable: true,
            filterOperators: equalsOperator,
            renderCell: ({ row }) => {
                const status = row?.status ?? '';
                const isValidated = status === 'Validated';
                const src = isValidated ? '/images/validatedIcon.svg' : '/images/invalidIcon.svg';
                const label = isValidated ? 'validated' : status;
                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <img src={src} style={{ width: 16, height: 16 }} />
                        <span style={{ marginLeft: 8 }}>
                            {
                                <Tooltip placement="top" title={row.errorMessage} type="standard" arrow>
                                    {label}
                                </Tooltip>
                            }
                        </span>
                    </div>
                );
            },
        },
        {
            field: 'loginScheme',
            headerName: 'Login Method',
            id: uuidv4(),
            filterable: false,
            type: 'string',
            width: 130,
            renderCell: ({ row }) => {
                return loginProviderColumn(row);
            },
        },
        {
            field: 'mfaType',
            headerName: 'MFA',
            id: uuidv4(),
            type: 'string',
            width: 130,
            filterable: false,
            editable: true,
            renderCell: ({ row }) => {
                return mfaTypeColumn(row);
            },
        },
        {
            field: 'email',
            headerName: 'Email',
            id: uuidv4(),
            type: 'string',
            width: 130,
            filterable: true,
            editable: true,
            filterOperators: equalsOperator,
        },
        {
            field: 'address1',
            headerName: 'Address 1',
            id: uuidv4(),
            type: 'string',
            width: 120,
            editable: true,
            filterable: false,
        },
        {
            field: 'address2',
            headerName: 'Address 2',
            id: uuidv4(),
            type: 'string',
            width: 120,
            editable: true,
            filterable: false,
        },
        {
            field: 'city',
            headerName: 'City',
            id: uuidv4(),
            type: 'string',
            width: 120,
            editable: true,
            filterable: false,
        },
        {
            field: 'state',
            headerName: 'State',
            id: uuidv4(),
            type: 'string',
            width: 90,
            editable: true,
            filterable: false,
        },
        {
            field: 'zip',
            headerName: 'Zip Code',
            id: uuidv4(),
            type: 'string',
            width: 110,
            editable: true,
            filterable: false,
        },
        {
            field: 'preferredPhoneExt',
            headerName: 'Preferred Phone Ext',
            id: uuidv4(),
            type: 'string',
            width: 170,
            editable: true,
            filterable: false,
        },
        {
            field: 'preferredPhone',
            headerName: 'Preferred Phone',
            id: uuidv4(),
            type: 'string',
            width: 160,
            editable: true,
            filterable: false,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            id: uuidv4(),
            type: 'actions',
            hideable: false,
            cellClassName: 'actions',
            width: 100,
            align: 'right',
            getActions: ({ id, row }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
                const selectedRow = row.id;

                if (isInEditMode) {
                    return [
                        <Button
                            key={id}
                            color="ghost"
                            variant="outlined"
                            endIcon={<CheckIcon />}
                            onClick={handleSaveClick(id)}
                            size="small"
                            label="save-btn"
                            className="action-btn"
                        />,
                        <Button
                            key={id}
                            color="ghost"
                            variant="outlined"
                            endIcon={<CancelIcon />}
                            label="cancel-btn"
                            onClick={handleCancelClick(id)}
                            size="small"
                            className="action-btn"
                        />,
                    ];
                }
                return [<ActionMenu key={id} handleEditClick={handleEditClick(id)} rowId={selectedRow} />];
            },
        },
    ];
};

export default Columns;
